// src/core/shareCard.ts
// Pure layout for the 1080×1080 image share card. Produces a list of draw
// ops so the layout is unit-testable in node; src/ui/shareImage.ts paints
// the ops onto a real canvas in the browser.

import type { RoundResult } from '../types';

export const CARD_W = 1080;
export const CARD_H = 1080;

const BG = '#2a2622';          // same warm dark as the in-app share hero
const PAPER = '#f4f1ea';
const PAPER_DIM = 'rgba(244, 241, 234, 0.62)';
const SERIF = 'Georgia, "Times New Roman", serif';
const SANS = '-apple-system, "Helvetica Neue", Arial, sans-serif';

export type ShareCardInput = {
  dayNumber: number;
  dayName: string;
  total: number;
  streak: number;
  results: RoundResult[];
};

export type DrawOp =
  | { kind: 'rect'; x: number; y: number; w: number; h: number; fill: string }
  | {
      kind: 'text'; x: number; y: number; text: string; font: string; fill: string;
      align: CanvasTextAlign; baseline: CanvasTextBaseline;
    };

const PAD = 80;
const GAP = 24;
const COLS = 5;
const SWATCH_W = Math.floor((CARD_W - PAD * 2 - GAP * (COLS - 1)) / COLS); // 164
const SWATCH_H = 150;
const PAIRS_TOP = 560;

export function shareCardOps(input: ShareCardInput): DrawOp[] {
  const ops: DrawOp[] = [];
  ops.push({ kind: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, fill: BG });

  // Masthead
  ops.push({
    kind: 'text', x: PAD, y: PAD, text: `BARVE №${input.dayNumber}`,
    font: `500 26px ${SANS}`, fill: PAPER_DIM, align: 'left', baseline: 'top',
  });
  ops.push({
    kind: 'text', x: CARD_W - PAD, y: PAD, text: 'barve.app',
    font: `500 26px ${SANS}`, fill: PAPER_DIM, align: 'right', baseline: 'top',
  });

  // Puzzle name
  ops.push({
    kind: 'text', x: PAD, y: 150, text: input.dayName,
    font: `italic 44px ${SERIF}`, fill: PAPER, align: 'left', baseline: 'top',
  });

  // Score
  ops.push({
    kind: 'text', x: PAD, y: 400, text: String(input.total),
    font: `600 220px ${SERIF}`, fill: PAPER, align: 'left', baseline: 'alphabetic',
  });
  ops.push({
    kind: 'text', x: CARD_W - PAD, y: 400, text: '/100',
    font: `400 64px ${SERIF}`, fill: PAPER_DIM, align: 'right', baseline: 'alphabetic',
  });

  // Column labels
  ops.push({
    kind: 'text', x: PAD, y: PAIRS_TOP - 34, text: 'TARGET / YOUR GUESS',
    font: `500 20px ${SANS}`, fill: PAPER_DIM, align: 'left', baseline: 'top',
  });

  // Five pairs: target above guess, shared edge, round score below
  input.results.forEach((r, i) => {
    const x = PAD + i * (SWATCH_W + GAP);
    ops.push({
      kind: 'rect', x, y: PAIRS_TOP, w: SWATCH_W, h: SWATCH_H,
      fill: `hsl(${r.target.h}, ${r.target.s}%, ${r.target.l}%)`,
    });
    ops.push({
      kind: 'rect', x, y: PAIRS_TOP + SWATCH_H, w: SWATCH_W, h: SWATCH_H,
      fill: `hsl(${r.guess.h}, ${r.guess.s}%, ${r.guess.l}%)`,
    });
    ops.push({
      kind: 'text', x: x + SWATCH_W / 2, y: PAIRS_TOP + SWATCH_H * 2 + 22, text: String(r.score),
      font: `600 34px ${SERIF}`, fill: PAPER, align: 'center', baseline: 'top',
    });
  });

  // Footer
  if (input.streak >= 2) {
    ops.push({
      kind: 'text', x: PAD, y: CARD_H - PAD, text: `\u{1F525} ${input.streak} day streak`,
      font: `500 26px ${SANS}`, fill: PAPER_DIM, align: 'left', baseline: 'alphabetic',
    });
  }
  ops.push({
    kind: 'text', x: CARD_W - PAD, y: CARD_H - PAD, text: 'Can you match the color?',
    font: `italic 26px ${SERIF}`, fill: PAPER_DIM, align: 'right', baseline: 'alphabetic',
  });

  return ops;
}

export type PaintSurface = Pick<
  CanvasRenderingContext2D,
  'fillStyle' | 'fillRect' | 'font' | 'textAlign' | 'textBaseline' | 'fillText'
>;

export function paintOps(ctx: PaintSurface, ops: DrawOp[]): void {
  for (const op of ops) {
    ctx.fillStyle = op.fill;
    if (op.kind === 'rect') {
      ctx.fillRect(op.x, op.y, op.w, op.h);
    } else {
      ctx.font = op.font;
      ctx.textAlign = op.align;
      ctx.textBaseline = op.baseline;
      ctx.fillText(op.text, op.x, op.y);
    }
  }
}
